/**
 * TODO: Convert this to tsx, however before we do that we should first convert the base Carousel
 * component to tsx and export the types for the settings and ref props.
 * This will make the conversion easier and ensure type safety across both components.
 */
import React, {
  useRef,
  useState,
  useEffect,
  useCallback,
  Children,
  useMemo,
} from 'react'
import PropTypes from 'prop-types'
import { noop } from 'lodash'
import classnames from 'classnames/bind'
import Carousel from '../carousel'
import Icon from '../../elementals/icon'
import { ColorNames } from '../../../constants/colorVars'

import styles from './styles.scss'

const cx = classnames.bind(styles)

const baseSettings = {
  variableWidth: true,
  infinite: false,
  slidesToScroll: 1,
  swipe: true,
  touchMove: true,
  swipeToSlide: true,
}

const SCROLL_ANIMATION_MS = 300
const SCROLL_BUFFER_PX = 2
const GAP_PX = 6

const EnhancedCarousel = ({
  children,
  header = '',
  subheader = '',
  headerLink = '',
  arrowIconProps = {},
  elmId = 'enhanced-carousel',
  carouselRef: externalRef,
  next = noop,
  back = noop,
  settings = {},
  afterChangeCallback = noop,
  onHeaderClick = noop,
  ...carouselProps
}) => {
  const internalRef = useRef()
  const carouselRef = externalRef || internalRef
  const containerRef = useRef()
  const currentOffsetRef = useRef(0)
  const isInViewportRef = useRef(false)

  const lastWidthRef = useRef(0)

  const [canScrollPrev, setCanScrollPrev] = useState(false)
  const [canScrollNext, setCanScrollNext] = useState(true)
  const [allContentFits, setAllContentFits] = useState(false)

  const childCount = Children.count(children)

  const getSlideContent = useCallback(slide => {
    const slickWrapper = slide.querySelector(':scope > div')
    if (slickWrapper?.firstElementChild) {
      return slickWrapper.firstElementChild
    }
    return slide
  }, [])

  const updateScrollState = useCallback(() => {
    const slickList = containerRef.current?.querySelector('.slick-list')
    const slickTrack = containerRef.current?.querySelector('.slick-track')
    const slides = containerRef.current?.querySelectorAll(
      '.slick-slide:not(.slick-cloned)'
    )

    if (!slickList || !slickTrack || !slides?.length) return

    const containerRect = slickList.getBoundingClientRect()
    const firstContent = getSlideContent(slides[0])
    const lastContent = getSlideContent(slides[slides.length - 1])
    const firstContentRect = firstContent.getBoundingClientRect()
    const lastContentRect = lastContent.getBoundingClientRect()

    const allVisible =
      firstContentRect.left >= containerRect.left - SCROLL_BUFFER_PX &&
      lastContentRect.right <= containerRect.right + SCROLL_BUFFER_PX

    setAllContentFits(allVisible)

    if (allVisible) {
      setCanScrollPrev(false)
      setCanScrollNext(false)
      return
    }

    setCanScrollPrev(
      firstContentRect.left < containerRect.left - SCROLL_BUFFER_PX
    )
    setCanScrollNext(
      lastContentRect.right > containerRect.right + SCROLL_BUFFER_PX
    )
  }, [getSlideContent])

  useEffect(() => {
    const initTimeout = setTimeout(() => {
      currentOffsetRef.current = 0
      lastWidthRef.current = containerRef.current?.offsetWidth ?? 0
      updateScrollState()
    }, 200)

    const handleResize = () => {
      const currentWidth = containerRef.current?.offsetWidth ?? 0
      if (currentWidth === lastWidthRef.current) return
      lastWidthRef.current = currentWidth

      currentOffsetRef.current = 0
      const slickTrack = containerRef.current?.querySelector('.slick-track')
      if (slickTrack) {
        slickTrack.style.transform = 'translate3d(0px, 0px, 0px)'
      }
      updateScrollState()
    }

    window.addEventListener('resize', handleResize)

    return () => {
      clearTimeout(initTimeout)
      window.removeEventListener('resize', handleResize)
    }
  }, [updateScrollState, childCount])

  // Wheel/trackpad horizontal scroll support
  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    let rafId = null

    const handleWheel = e => {
      const slickTrack = container.querySelector('.slick-track')
      const slickList = container.querySelector('.slick-list')
      const slides = container.querySelectorAll(
        '.slick-slide:not(.slick-cloned)'
      )

      if (!slickTrack || !slickList || !slides?.length) return

      // Only respond to predominantly horizontal trackpad scrolling —
      // ignore vertical scroll, mouse wheel, and diagonal gestures
      // so the page can scroll normally
      if (Math.abs(e.deltaX) <= Math.abs(e.deltaY) || e.deltaX === 0) return
      const delta = e.deltaX

      // Compute max offset so we don't scroll past the last card
      const lastSlide = slides[slides.length - 1]
      const lastContent = getSlideContent(lastSlide)
      const maxOffset =
        lastContent.offsetLeft + lastContent.offsetWidth - slickList.offsetWidth

      const currentOffset = getCurrentOffset(slickTrack)

      // At boundaries, don't capture — let the page scroll normally
      if (delta > 0 && currentOffset >= maxOffset) return
      if (delta < 0 && currentOffset <= 0) return

      e.preventDefault()

      if (rafId) cancelAnimationFrame(rafId)

      rafId = requestAnimationFrame(() => {
        const newOffset = Math.max(
          0,
          Math.min(maxOffset, currentOffset + delta)
        )
        currentOffsetRef.current = newOffset
        slickTrack.style.transform = `translate3d(-${newOffset}px, 0px, 0px)`
        updateScrollState()
      })
    }

    container.addEventListener('wheel', handleWheel, { passive: false })

    return () => {
      container.removeEventListener('wheel', handleWheel)
      if (rafId) cancelAnimationFrame(rafId)
    }
  }, [getSlideContent, updateScrollState])

  const getVisibleSlideIndices = useCallback(() => {
    const slickList = containerRef.current?.querySelector('.slick-list')
    const slides = containerRef.current?.querySelectorAll(
      '.slick-slide:not(.slick-cloned)'
    )

    if (!slickList || !slides?.length) return []

    const containerRect = slickList.getBoundingClientRect()
    const visibleIndices = []

    slides.forEach((slide, index) => {
      const content = getSlideContent(slide)
      const rect = content.getBoundingClientRect()
      const isVisible =
        rect.right > containerRect.left + SCROLL_BUFFER_PX &&
        rect.left < containerRect.right - SCROLL_BUFFER_PX

      if (isVisible) {
        visibleIndices.push(index)
      }
    })

    return visibleIndices
  }, [getSlideContent])

  const handleAfterChange = useCallback(() => {
    setTimeout(() => {
      // Sync currentOffsetRef with slick's actual position after native drag
      const slickTrack = containerRef.current?.querySelector('.slick-track')
      if (slickTrack) {
        currentOffsetRef.current = getCurrentOffset(slickTrack)
      }
      updateScrollState()
      if (afterChangeCallback && isInViewportRef.current) {
        const visibleIndices = getVisibleSlideIndices()
        afterChangeCallback(visibleIndices)
      }
    }, 50)
  }, [updateScrollState, afterChangeCallback, getVisibleSlideIndices])

  useEffect(() => {
    if (!afterChangeCallback || !containerRef.current) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        const wasInViewport = isInViewportRef.current
        isInViewportRef.current = entry.isIntersecting

        if (entry.isIntersecting && !wasInViewport) {
          const visibleIndices = getVisibleSlideIndices()
          afterChangeCallback(visibleIndices)
        }
      },
      { threshold: 0.1 }
    )

    observer.observe(containerRef.current)

    return () => observer.disconnect()
  }, [afterChangeCallback, getVisibleSlideIndices])

  const mergedSettings = useMemo(
    () => ({
      ...baseSettings,
      draggable: true,
      ...settings,
      afterChange: handleAfterChange,
    }),
    [settings, handleAfterChange]
  )

  const getScrollStep = slides => {
    const firstContent = getSlideContent(slides[0])
    return firstContent.offsetWidth + GAP_PX
  }

  const getCurrentOffset = slickTrack => {
    const transform = slickTrack.style.transform || ''
    const match = transform.match(/translate3d\((-?[\d.]+)px/)
    return match ? Math.abs(parseFloat(match[1])) : 0
  }

  const animateScroll = (slickTrack, newOffset, callback) => {
    currentOffsetRef.current = newOffset
    slickTrack.style.transition = `transform ${SCROLL_ANIMATION_MS}ms ease`
    slickTrack.style.transform = `translate3d(-${newOffset}px, 0px, 0px)`

    setTimeout(() => {
      slickTrack.style.transition = ''
      updateScrollState()
      if (afterChangeCallback && isInViewportRef.current) {
        const visibleIndices = getVisibleSlideIndices()
        afterChangeCallback(visibleIndices)
      }
      callback?.()
    }, SCROLL_ANIMATION_MS + 50)
  }

  const handlePrevClick = e => {
    if (!canScrollPrev) return

    const slickTrack = containerRef.current?.querySelector('.slick-track')
    const slides = containerRef.current?.querySelectorAll(
      '.slick-slide:not(.slick-cloned)'
    )

    if (!slickTrack || !slides?.length) return

    const currentOffset = getCurrentOffset(slickTrack)
    const scrollStep = getScrollStep(slides)
    const newOffset = Math.max(0, currentOffset - scrollStep)

    animateScroll(slickTrack, newOffset, () => {
      arrowIconProps?.onClick?.(e)
      back?.(e)
    })
  }

  const handleNextClick = e => {
    if (!canScrollNext) return

    const slickTrack = containerRef.current?.querySelector('.slick-track')
    const slides = containerRef.current?.querySelectorAll(
      '.slick-slide:not(.slick-cloned)'
    )

    if (!slickTrack || !slides?.length) return

    const currentOffset = getCurrentOffset(slickTrack)
    const scrollStep = getScrollStep(slides)
    const newOffset = currentOffset + scrollStep

    animateScroll(slickTrack, newOffset, () => {
      arrowIconProps?.onClick?.(e)
      next?.(e)
    })
  }

  const handleKeyDown = (e, handler) => {
    if (e.key === 'Enter') handler(e)
  }

  return (
    <div
      ref={containerRef}
      className={cx('enhanced-carousel-wrapper')}
      data-elm-id={`${elmId}_wrapper`}
    >
      {(header || subheader) && (
        <div
          className={cx('header-section', { 'has-subheader': !!subheader })}
          data-elm-id={`${elmId}_header`}
        >
          <div className={cx('header-left')}>
            {header &&
              (headerLink ? (
                <a
                  href={headerLink}
                  target='_blank'
                  rel='noopener noreferrer'
                  className={cx('header-text', 'clickable')}
                  data-elm-id={`${elmId}_header_link`}
                  onClick={onHeaderClick}
                >
                  {header}
                  <Icon
                    className={cx('header-chevron')}
                    name='chevron-right'
                    iconStyle='solid'
                    color={ColorNames.GrayDark}
                    size='small'
                  />
                </a>
              ) : (
                <span
                  className={cx('header-text')}
                  data-elm-id={`${elmId}_header_text`}
                >
                  {header}
                </span>
              ))}
            {subheader && (
              <span
                className={cx('subheader-text')}
                data-elm-id={`${elmId}_subheader_text`}
              >
                {subheader}
              </span>
            )}
          </div>

          {!allContentFits && (
            <div className={cx('header-right')}>
              <div
                role='button'
                tabIndex={canScrollPrev ? 0 : -1}
                onClick={handlePrevClick}
                onKeyDown={e => handleKeyDown(e, handlePrevClick)}
                className={cx('nav-button', { disabled: !canScrollPrev })}
                data-elm-id={`${elmId}_prev_button`}
              >
                <Icon
                  name='chevron-left'
                  iconStyle='regular'
                  color={
                    canScrollPrev ? ColorNames.Gray : ColorNames.GrayMedium
                  }
                  size='medium'
                />
              </div>
              <div
                role='button'
                tabIndex={canScrollNext ? 0 : -1}
                onClick={handleNextClick}
                onKeyDown={e => handleKeyDown(e, handleNextClick)}
                className={cx('nav-button', { disabled: !canScrollNext })}
                data-elm-id={`${elmId}_next_button`}
              >
                <Icon
                  name='chevron-right'
                  iconStyle='regular'
                  color={
                    canScrollNext ? ColorNames.Gray : ColorNames.GrayMedium
                  }
                  size='medium'
                />
              </div>
            </div>
          )}
        </div>
      )}

      <Carousel
        {...carouselProps}
        settings={mergedSettings}
        carouselRef={carouselRef}
        alwaysShowArrows={false}
        alwaysHideArrows
        elmId={elmId}
      >
        {children}
      </Carousel>
    </div>
  )
}

EnhancedCarousel.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]).isRequired,
  header: PropTypes.string,
  subheader: PropTypes.string,
  headerLink: PropTypes.string,
  settings: PropTypes.object,
  arrowIconProps: PropTypes.object,
  elmId: PropTypes.string,
  next: PropTypes.func,
  back: PropTypes.func,
  carouselRef: PropTypes.object,
  afterChangeCallback: PropTypes.func,
  onHeaderClick: PropTypes.func,
}

export default EnhancedCarousel
